import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { forwardRef, useState } from 'react';
import Nav from 'react-bootstrap/Nav';
import NavDropdown from 'react-bootstrap/NavDropdown';
import LibNavbar from 'react-bootstrap/Navbar';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { theme } from '_constants/theme';

import { ReactComponent as LogoDesktopSvg } from 'assets/images/logo-desktop.svg';
import { ReactComponent as LogoMobileSvg } from 'assets/images/logo-mobile.svg';
import { ReactComponent as MenuBurgerSvg } from 'assets/images/menu-burger.svg';
import { ReactComponent as MenuCloseSvg } from 'assets/images/menu-close.svg';
import { ReactComponent as MenuMoreSvg } from 'assets/images/menu-more.svg';

import Icon from 'components/atomic/Icon';
import Popup from 'components/atomic/Popup';
import Select from 'components/atomic/controls/Select';

import UserInfo from './UserInfo';

const StyledContainer = styled.div`
  .navbar {
    padding-top: ${theme.spaces.small2X}px;
    padding-bottom: ${theme.spaces.small2X}px;

    .navbar-brand {
      display: inline-flex;
      align-items: center;

      padding-top: 0;
      padding-bottom: 0;
      margin-right: ${theme.spaces.regular * 4}px;

      a {
        display: inline-flex;
        align-items: center;
      }
    }

    .navbar-toggler {
      border: none;
      padding: unset;

      &:focus {
        outline: unset;
        box-shadow: none;
      }
    }

    .navbar-collapse {
      .navbar-nav {
        .nav-link {
          display: inline-flex;
          align-items: center;
          position: relative;
          padding-left: ${theme.spaces.small}px;
          padding-right: ${theme.spaces.small}px;
          font-family: 'Inter';
          font-size: ${theme.fontSizes.small}px;
          font-weight: 400;
          line-height: 24px;
          text-align: right;
          text-decoration: none;
          color: ${theme.colors.indigo.light};

          &:hover {
            text-decoration: underline;
          }
        }

        .nav-item {
          .dropdown-toggle {
            > span {
              display: inline-flex;
              align-items: center;
            }
          }

          .dropdown-menu {
            border: 1px solid ${theme.colors.indigo.superLight};
            background: ${theme.colors.indigo.superLight};

            .dropdown-item {
              padding-top: ${theme.spaces.smallX}px;
              padding-bottom: ${theme.spaces.smallX}px;
              font-family: 'Inter';
              font-size: ${theme.fontSizes.small}px;
              font-weight: 400;
              line-height: 24px;
              text-align: left;
              color: ${theme.colors.indigo.dark};

              &:hover {
                background: ${theme.colors.indigo.dark};
                color: ${theme.colors.indigo.superLight};
              }
            }
          }
        }
      }

      button {
        margin-left: ${theme.spaces.small}px;
      }
    }
  }

  @media ${`(min-width: ${theme.displaySizes.tablet})`} {
    .navbar {
      .navbar-nav {
        .nav-item {
          &.dropdown:hover {
            .dropdown-menu {
              display: block;
              margin-top: 0;
            }
          }
        }
      }
    }
  }

  @media ${theme.devices.tablet} {
    .navbar {
      min-height: 48px;

      .navbar-brand {
        padding-top: ${theme.spaces.smallX}px;
        padding-bottom: ${theme.spaces.smallX}px;
        margin-right: ${theme.spaces.regular}px;
      }

      .navbar-toggler {
        flex-grow: 1;
        text-align: left;
      }

      &.open {
        .navbar-toggler {
          text-align: right;
        }
      }

      .navbar-collapse {
        .navbar-nav {
          padding-top: ${theme.spaces.regular}px;
          padding-bottom: ${theme.spaces.regular}px;

          .nav-item {
            .dropdown-toggle {
              display: inline-flex;
              align-items: center;
              width: 100%;

              &:after {
                margin-right: 0.255em;
              }
            }

            .dropdown-menu {
              margin-left: 8px;
              margin-right: 8px;
              border-radius: unset;
              border-color: ${theme.colors.indigo.light};
              background: ${theme.colors.indigo.dark};

              .dropdown-item {
                padding-left: ${theme.spaces.smallX}px;
                padding-right: ${theme.spaces.smallX}px;
                color: ${theme.colors.indigo.light};
              }
            }
          }
        }
      }
    }
  }
`;

// eslint-disable-next-line react/display-name, react/prop-types, no-unused-vars
const StyledSvgContainer = styled(forwardRef(({ desktop, mobile, ...props }, ref) => <span ref={ref} {...props} />))`
  display: inline-flex;
  justify-content: center;
  align-items: center;

  ${({ mobile }) =>
    mobile &&
    css`
      display: none;
    `}

  @media ${theme.devices.tablet} {
    ${({ desktop }) =>
      desktop &&
      css`
        display: none;
      `}

    ${({ mobile }) =>
      mobile &&
      css`
        display: inline-flex;
      `}
  }
`;

const StyledIconContainer = styled.span`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  margin-right: ${theme.spaces.smallX - 2}px;
`;

const StyledSettingContainer = styled.div`
  display: flex;
  align-items: center;
`;

const StyledRoundedContainer = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  height: 36px;
  width: 36px;
  margin-left: ${theme.spaces.regular}px;
  border-radius: 40px;
  background: ${theme.colors.indigo.primary};
  cursor: pointer;
`;

const StyledExtraLinksContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 160px;
  padding: ${theme.spaces.regular}px;

  a {
    display: inline-block;
    margin-bottom: ${theme.spaces.smallX}px;
    font-family: 'Inter';
    font-size: ${theme.fontSizes.small}px;
    font-weight: 400;
    line-height: 24px;
    text-align: left;
    text-decoration: none;
    color: ${theme.colors.indigo.dark};

    &:last-child {
      margin-bottom: 0;
    }

    &:hover {
      text-decoration: underline;
    }
  }
`;

const StyledSelectContainer = styled.div`
  width: 230px;
  margin-right: ${theme.spaces.regular}px;

  @media ${theme.devices.tablet} {
    width: unset;
    margin-right: 0;
    margin-bottom: ${theme.spaces.regular}px;
  }
`;

const StyledBadge = styled.div`
  position: absolute;
  top: 2px;
  left: 74px;
  padding-top: 2px;
  padding-bottom: 2px;
  padding-left: 4px;
  padding-right: 4px;
  border-radius: 10px;
  background-color: #dc3545;
  font-size: 10px;
  line-height: 10px;
  font-weight: 700;
  color: #ffffff;
`;

// eslint-disable-next-line react/prop-types
const NavDropdownItem = ({ iconAlias, title, path }) => {
  return (
    <NavDropdown.Item to={`..${path}`} as={Link}>
      <StyledIconContainer>
        <Icon alias={iconAlias} color={theme.colors.indigo.light} />
      </StyledIconContainer>
      {title}
    </NavDropdown.Item>
  );
};

// eslint-disable-next-line react/prop-types
const NavItem = ({ iconAlias, title, path, subitems, countOfUnread }) => {
  if (subitems) {
    return (
      <NavDropdown
        title={
          <span>
            <StyledIconContainer>
              <Icon alias={iconAlias} color={theme.colors.indigo.light} />
            </StyledIconContainer>
            {title}
            {Boolean(countOfUnread) && <StyledBadge>{countOfUnread > 99 ? '99+' : countOfUnread}</StyledBadge>}
          </span>
        }
        // eslint-disable-next-line react/prop-types
        id={`nav-dropdown-${subitems[0].path}`}
        renderMenuOnMount={true}
      >
        {/* eslint-disable-next-line react/prop-types */}
        {subitems.map((item) => (
          <NavDropdownItem key={item.path} {...item} />
        ))}
      </NavDropdown>
    );
  }

  return (
    <Nav.Link to={`..${path}`} as={Link}>
      <StyledIconContainer>
        <Icon alias={iconAlias} color={theme.colors.indigo.light} />
      </StyledIconContainer>
      {title}
      {Boolean(countOfUnread) && <StyledBadge>{countOfUnread > 99 ? '99+' : countOfUnread}</StyledBadge>}
    </Nav.Link>
  );
};

const Navbar = ({ items, userDetails, activeOrderId, orderOptions, onOrderSelect }) => {
  const [open, setOpen] = useState(false);

  const handleOrderSelect = (e) => {
    const { value } = e.target;
    onOrderSelect && onOrderSelect(value);
  };

  return (
    <StyledContainer>
      <LibNavbar collapseOnSelect expand="md" className={clsx({ open })} onToggle={setOpen}>
        <LibNavbar.Brand>
          <Link to="/">
            <StyledSvgContainer desktop>
              <LogoDesktopSvg />
            </StyledSvgContainer>
            <StyledSvgContainer mobile>
              <LogoMobileSvg />
            </StyledSvgContainer>
          </Link>
        </LibNavbar.Brand>
        <LibNavbar.Toggle aria-controls="main-navbar-nav">
          {!open && (
            <StyledSvgContainer>
              <MenuBurgerSvg />
            </StyledSvgContainer>
          )}
          {open && (
            <StyledSvgContainer>
              <MenuCloseSvg />
            </StyledSvgContainer>
          )}
        </LibNavbar.Toggle>
        <LibNavbar.Collapse id="main-navbar-nav">
          <Nav className="me-auto">
            {items.map((item) => (
              <NavItem key={item.path} {...item} />
            ))}
          </Nav>
          <StyledSelectContainer>
            <Select
              searchable
              placeholder="Select Order..."
              borderColor={theme.colors.indigo.dark}
              backgroundColor={theme.colors.indigo.superLight}
              placeholderColor={theme.colors.indigo.light}
              textColor={theme.colors.indigo.dark}
              options={orderOptions}
              value={activeOrderId}
              onChange={handleOrderSelect}
            />
          </StyledSelectContainer>
        </LibNavbar.Collapse>
        {!open && (
          <StyledSettingContainer>
            <UserInfo name={userDetails?.monexaUsername} />
            <Popup
              trigger={
                <StyledRoundedContainer>
                  <MenuMoreSvg />
                </StyledRoundedContainer>
              }
            >
              <StyledExtraLinksContainer>
                <Link to="../settings/account">Settings</Link>
                <Link to="../logout">Logout</Link>
              </StyledExtraLinksContainer>
            </Popup>
          </StyledSettingContainer>
        )}
      </LibNavbar>
    </StyledContainer>
  );
};

Navbar.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.node,
      path: PropTypes.string,
      subitems: PropTypes.shape({
        title: PropTypes.node,
        path: PropTypes.string,
      }),
      countOfUnread: PropTypes.number,
    })
  ),
  userDetails: PropTypes.object,
  activeOrderId: PropTypes.string,
  orderOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.node,
      value: PropTypes.string,
    })
  ),
  onOrderSelect: PropTypes.func,
};

export default Navbar;
